import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { WidgetArticleSliderType, WidgetDisplayMode, formatImgUrl, jFileLittle } from 'jamespot-user-api';
import styled from 'styled-components';
import { Widget, WidgetEditor } from 'jamespot-front-business';
import { shallowEqual } from 'react-redux';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const FileViewer = commonComponentsRegistry.getLazyComponent('FileViewer');
const JRCWidgetEmptyInplace = commonComponentsRegistry.getLazyComponent('WidgetEmptyInplace');
const JRCWidgetSlider = commonComponentsRegistry.getLazyComponent('WidgetSlider');

const Wrapper = styled.div<{ maxWidth: number }>`
    flex: 1;
    display: flex;
    flex-direction: row;
    min-height: 60px;
    align-items: center;
    width: ${(props) => props.maxWidth}px;
`;

const mockFileLittle: jFileLittle = {
    id: 0,
    type: '',
    title: '',
    uri: '',
    mimetype: 'application/octet-stream',
    mainType: '',
    dateCreation: '',
    dateModified: '',
    _url: '',
    size: 10,
};

export const strToTimestamp = (str: string): undefined | number => {
    if (str) {
        const timestamp = Date.parse(str);
        if (!isNaN(timestamp)) {
            return new Date(timestamp).getTime() / 1000;
        }
    }
    return;
};

export const JRCWidgetArticleSliderWrapper = ({
    widget,
    mode,
    width,
}: {
    widget: WidgetArticleSliderType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
    width?: number;
}) => {
    const { uniqid } = widget;
    const dispatch = useAppDispatch();
    const isActive = useAppSelector((state) => Widget.selectors.isActive(state, uniqid), shallowEqual);
    const maxWidth = width ?? 600;
    const [dynamicWidth, setDynamicWidth] = useState<number | undefined>(undefined);
    const ref = useRef<HTMLDivElement>(null);
    const fileViewerFiles = useMemo(
        () =>
            widget.content.slides
                ? [
                      ...widget.content.slides.map((slide, index) => {
                          return {
                              ...mockFileLittle,
                              id: slide.file ? slide.file.id : index,
                              mimetype: slide.file ? slide.file.mimetype : mockFileLittle.mimetype,
                              path:
                                  slide.file && slide.file.uri
                                      ? formatImgUrl({
                                            size: 'fitx1200',
                                            uri: slide.file.uri,
                                            from: 'imagecache',
                                            timestamp:
                                                slide.file && slide.file.dateModified
                                                    ? strToTimestamp(slide.file.dateModified)
                                                    : Date.now(),
                                        })
                                      : '',
                          };
                      }),
                  ]
                : [],
        [widget.content.slides],
    );

    const slides = useMemo(
        () =>
            widget.content.slides !== undefined
                ? [
                      ...widget.content.slides.map((slide) => {
                          if (slide.file) {
                              return {
                                  ...slide,
                                  uri: formatImgUrl({
                                      timestamp: slide.file.dateModified
                                          ? strToTimestamp(slide.file.dateModified)
                                          : Date.now(),
                                      size: `fitx${dynamicWidth && dynamicWidth < maxWidth ? dynamicWidth : maxWidth}`,
                                      from: 'imagecache',
                                      uri: slide.file.uri,
                                  }),
                              };
                          } else {
                              return {
                                  ...slide,
                                  uri: '',
                              };
                          }
                      }),
                  ]
                : [],
        [widget.content.slides, dynamicWidth, maxWidth],
    );

    useEffect(() => {
        if (isActive && mode === 'edit') {
            dispatch(WidgetEditor.slice.actions.registerEditor({ uniqid, name: widget.name }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    useEffect(() => {
        const handleResize = () => {
            if (ref.current) {
                setDynamicWidth(ref.current ? ref.current.offsetWidth : maxWidth);
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    useEffect(() => {
        setDynamicWidth(ref.current ? ref.current.offsetWidth : maxWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    useEffect(() => {
        dispatch(
            Widget.slice.actions.setWidgetState({
                uniqid,
                empty: !widget.content.slides || widget.content.slides.length === 0,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widget.content.slides]);

    const isEditable = useMemo(
        () => mode === 'edit' && Array.isArray(widget.content.slides) && widget.content.slides.length === 0,
        [widget.content.slides, mode],
    );

    const handleEditor = useCallback(() => {
        dispatch(
            WidgetEditor.slice.actions.registerEditor({
                uniqid: widget.uniqid,
                name: widget.name,
            }),
        );
    }, [dispatch, widget.uniqid, widget.name]);

    /** small hack to catch the real width of the div */
    if (dynamicWidth === undefined) {
        return <div ref={ref} style={{ width: '100%' }}></div>;
    }

    return (
        <Wrapper maxWidth={dynamicWidth && dynamicWidth < maxWidth ? dynamicWidth : maxWidth}>
            <FileViewer files={fileViewerFiles} openWithOptions={[]}>
                {(open) => {
                    return (
                        <JRCWidgetSlider
                            slides={slides}
                            maxWidth={dynamicWidth && dynamicWidth < maxWidth ? dynamicWidth : maxWidth}
                            useDots={widget.content.useDots ?? true}
                            loop={widget.content.loop ?? false}
                            startAt={0}
                            onClickSlide={(index) => {
                                if (mode !== 'edit') {
                                    if (widget.content.slides) {
                                        const slide = widget.content.slides[index];
                                        if (slide && slide.file) {
                                            open(slide.file.id);
                                        }
                                    }
                                } else {
                                    dispatch(WidgetEditor.slice.actions.registerEditor({ uniqid, name: widget.name }));
                                }
                            }}
                        />
                    );
                }}
            </FileViewer>
            {isEditable ? (
                <JRCWidgetEmptyInplace
                    label={'WIDGET_Article_Slider_Empty_Label'}
                    description={'WIDGET_Article_Slider_Empty_Description'}
                    url={'widget-article-image-placeholder.png'}
                    onClick={handleEditor}
                />
            ) : (
                <></>
            )}
        </Wrapper>
    );
};
